// AdminPanel.js - Versione Completa con Basic Auth Migliorato
import React, { useState, useEffect } from "react"
import { fetchProducts, fetchMaterialLinks, fetchCategories } from "./api"
import ProductsTab from "./components/admin/ProductsTab"
import MaterialsTab from "./components/admin/MaterialsTab"
import ViewsTab from "./components/admin/ViewsTab"
import DashboardTab from "./components/admin/DashboardTab"
import ImportExportTab from "./components/admin/ImportExportTab"
import { withBasicAuth, useBasicAuth } from "./ImprovedBasicAuth" // MODIFICATO: Basic Auth
import logo from "./assets/logo-nudesign.png"

const AdminPanel = ({
  selectedProductCode,
  onProductChange,
  isOverlay = false,
  activeTab: externalActiveTab,
  onLogout: externalLogout, // NUOVO: Logout passato dal HOC
}) => {
  const [activeTab, setActiveTab] = useState("products")
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [materialLinks, setMaterialLinks] = useState([])
  const [selectedProduct, setSelectedProduct] = useState(null)

  // MODIFICATO: Hook per Basic Auth
  const { logout } = useBasicAuth()

  // Sync with external activeTab prop if provided (for overlay mode)
  useEffect(() => {
    if (externalActiveTab && externalActiveTab !== activeTab) {
      setActiveTab(externalActiveTab)
    }
  }, [externalActiveTab])

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true)
      try {
        const [productsData, categoriesData, linksData] = await Promise.all([
          fetchProducts(),
          fetchCategories(),
          fetchMaterialLinks(),
        ])

        setProducts(productsData)
        setCategories(categoriesData)
        setMaterialLinks(linksData)
      } catch (error) {
        console.error("Failed to load initial data:", error)
      } finally {
        setLoading(false)
      }
    }

    loadInitialData()
  }, [])

  // Find the selected product when product code changes
  useEffect(() => {
    if (selectedProductCode && products.length > 0) {
      const product = products.find((p) => p.code === selectedProductCode)
      setSelectedProduct(product || null)
    } else {
      setSelectedProduct(null)
    }
  }, [selectedProductCode, products])

  // Handle tab switching
  const handleTabChange = (tabName) => {
    setActiveTab(tabName)

    // Comunicare il tab attivo al parent se è in overlay mode
    if (isOverlay && window.adminOverlayRef) {
      window.adminOverlayRef.setActiveTab(tabName)
    }
  }

  // MODIFICATO: Gestione logout con Basic Auth
  const handleLogout = () => {
    if (confirm("Sei sicuro di voler uscire dall'area admin?")) {
      logout()
      if (externalLogout) {
        externalLogout()
      }
    }
  }

  // Loading overlay component
  const LoadingOverlay = () => (
    <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50">
      <div className="ay-bg-white ay-p-6 ay-rounded-lg ay-shadow-lg ay-w-64">
        <div className="ay-animate-spin ay-w-12 ay-h-12 ay-border-4 ay-border-blue-500 ay-border-t-transparent ay-rounded-full ay-mx-auto"></div>
        <p className="ay-mt-4 ay-text-center ay-text-lg">Loading...</p>
      </div>
    </div>
  )

  // Tabs component - separato per essere riutilizzabile
  const TabsComponent = () => (
    <div
      className={`ay-flex ay-mb-6 ay-bg-white ay-rounded-lg ay-shadow ay-overflow-x-auto ${
        isOverlay ? "ay-mb-4" : ""
      }`}
    >
      <TabButton
        active={activeTab === "dashboard"}
        onClick={() => handleTabChange("dashboard")}
        icon="📊"
        text="Dashboard"
        isOverlay={isOverlay}
      />
      <TabButton
        active={activeTab === "products"}
        onClick={() => handleTabChange("products")}
        icon="📦"
        text="Prodotti"
        isOverlay={isOverlay}
      />
      <TabButton
        active={activeTab === "materials"}
        onClick={() => handleTabChange("materials")}
        icon="🎨"
        text="Materiali"
        isOverlay={isOverlay}
      />
      <TabButton
        active={activeTab === "import"}
        onClick={() => handleTabChange("import")}
        icon="📥"
        text="Import/Export"
        isOverlay={isOverlay}
      />
      {selectedProductCode && (
        <TabButton
          active={activeTab === "views"}
          onClick={() => handleTabChange("views")}
          icon="📹"
          text="Custom Views"
          isOverlay={isOverlay}
          isSpecial={true}
        />
      )}
    </div>
  )

  // Content component - separato per essere riutilizzabile
  const TabContentComponent = () => (
    <div
      className={`ay-bg-white ay-rounded-lg ay-shadow ${
        isOverlay ? "ay-h-full ay-overflow-hidden" : "ay-p-6"
      }`}
    >
      <div className={isOverlay ? "ay-h-full" : ""}>
        {activeTab === "dashboard" && (
          <div className={isOverlay ? "ay-p-4 ay-h-full ay-overflow-auto" : ""}>
            <DashboardTab
              products={products}
              categories={categories}
              materialLinks={materialLinks}
              setLoading={setLoading}
            />
          </div>
        )}

        {activeTab === "products" && (
          <div className={isOverlay ? "ay-p-4 ay-h-full ay-overflow-auto" : ""}>
            <ProductsTab
              products={products}
              setProducts={setProducts}
              categories={categories}
              setCategories={setCategories}
              onProductChange={onProductChange}
              setLoading={setLoading}
            />
          </div>
        )}

        {activeTab === "materials" && (
          <div className={isOverlay ? "ay-p-4 ay-h-full ay-overflow-auto" : ""}>
            <MaterialsTab
              products={products}
              setProducts={setProducts}
              setLoading={setLoading}
            />
          </div>
        )}

        {activeTab === "import" && (
          <div className={isOverlay ? "ay-p-4 ay-h-full ay-overflow-auto" : ""}>
            <ImportExportTab
              products={products}
              setProducts={setProducts}
              setLoading={setLoading}
            />
          </div>
        )}

        {activeTab === "views" && selectedProductCode && (
          <ViewsTab
            selectedProductCode={selectedProductCode}
            selectedProduct={selectedProduct}
            setLoading={setLoading}
            isOverlay={isOverlay}
          />
        )}
      </div>
    </div>
  )

  // Layout per overlay mode
  if (isOverlay) {
    return (
      <div className="ay-h-full ay-flex ay-flex-col ay-relative ay-bg-white">
        {/* Loading Overlay for overlay mode */}
        {loading && (
          <div className="ay-absolute ay-inset-0 ay-bg-white ay-bg-opacity-90 ay-flex ay-items-center ay-justify-center ay-z-10">
            <div className="ay-bg-white ay-p-4 ay-rounded-lg ay-shadow-lg">
              <div className="ay-animate-spin ay-w-8 ay-h-8 ay-border-4 ay-border-blue-500 ay-border-t-transparent ay-rounded-full ay-mx-auto"></div>
              <p className="ay-mt-2 ay-text-center ay-text-sm">Loading...</p>
            </div>
          </div>
        )}

        {/* Tabs per overlay - compatti */}
        <div className="ay-flex-shrink-0 ay-border-b ay-border-gray-200">
          <div className="ay-flex ay-overflow-x-auto">
            <TabButton
              active={activeTab === "dashboard"}
              onClick={() => handleTabChange("dashboard")}
              icon="📊"
              text="Dashboard"
              isOverlay={true}
            />
            <TabButton
              active={activeTab === "products"}
              onClick={() => handleTabChange("products")}
              icon="📦"
              text="Prodotti"
              isOverlay={true}
            />
            <TabButton
              active={activeTab === "materials"}
              onClick={() => handleTabChange("materials")}
              icon="🎨"
              text="Materiali"
              isOverlay={true}
            />
            <TabButton
              active={activeTab === "import"}
              onClick={() => handleTabChange("import")}
              icon="📥"
              text="Import/Export"
              isOverlay={true}
            />
            {selectedProductCode && (
              <TabButton
                active={activeTab === "views"}
                onClick={() => handleTabChange("views")}
                icon="📹"
                text="Custom Views"
                isOverlay={true}
                isSpecial={true}
              />
            )}
          </div>
        </div>

        {/* Content per overlay */}
        <div className="ay-flex-1 ay-overflow-hidden">
          {activeTab === "dashboard" && (
            <div className="ay-p-4 ay-h-full ay-overflow-auto">
              <DashboardTab
                products={products}
                categories={categories}
                materialLinks={materialLinks}
                setLoading={setLoading}
              />
            </div>
          )}

          {activeTab === "products" && (
            <div className="ay-p-4 ay-h-full ay-overflow-auto">
              <ProductsTab
                products={products}
                setProducts={setProducts}
                categories={categories}
                setCategories={setCategories}
                onProductChange={onProductChange}
                setLoading={setLoading}
              />
            </div>
          )}

          {activeTab === "materials" && (
            <div className="ay-p-4 ay-h-full ay-overflow-auto">
              <MaterialsTab
                products={products}
                setProducts={setProducts}
                setLoading={setLoading}
              />
            </div>
          )}

          {activeTab === "import" && (
            <div className="ay-p-4 ay-h-full ay-overflow-auto">
              <ImportExportTab
                products={products}
                setProducts={setProducts}
                setLoading={setLoading}
              />
            </div>
          )}

          {activeTab === "views" && selectedProductCode && (
            <ViewsTab
              selectedProductCode={selectedProductCode}
              selectedProduct={selectedProduct}
              setLoading={setLoading}
              isOverlay={isOverlay}
            />
          )}
        </div>
      </div>
    )
  }

  // Layout normale (fullscreen)
  return (
    <div className="ay-min-h-screen ay-bg-gray-50">
      {/* Loading Overlay */}
      {loading && <LoadingOverlay />}

      <div className="ay-container ay-mx-auto ay-p-4 ay-max-w-full">
        {/* Header with logo, title and logout button */}
        <div className="ay-flex ay-justify-between ay-items-center ay-mb-6 ay-bg-white ay-p-4 ay-rounded-lg ay-shadow ay-admin-header">
          <div className="ay-flex ay-items-center ay-gap-4">
            <img src={logo} alt="Logo" className="ay-h-12" />
            <div>
              <h1 className="ay-text-2xl ay-font-bold">Admin Panel</h1>
              <p className="ay-text-sm ay-text-gray-600">
                Gestione Configuratore 3D
              </p>
            </div>
          </div>

          {/* Buttons container con logout */}
          <div className="ay-flex ay-items-center ay-gap-3">
            <button
              onClick={handleLogout}
              className="ay-px-4 ay-py-2 ay-bg-gray-500 ay-text-white ay-rounded ay-hover:ay-bg-gray-600 ay-transition ay-flex ay-items-center ay-gap-2"
              title="Logout dall'area admin"
            >
              <svg
                className="ay-w-4 ay-h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Logout
            </button>
            <button
              onClick={() => (window.location.href = "/")}
              className="ay-px-4 ay-py-2 ay-bg-red-500 ay-text-white ay-rounded ay-hover:ay-bg-red-600 ay-transition"
            >
              Esci da Admin
            </button>
          </div>
        </div>

        {/* Tabs */}
        <TabsComponent />

        {/* Tab Content */}
        <TabContentComponent />
      </div>
    </div>
  )
}

// Componente helper per i tab buttons (invariato)
const TabButton = ({
  active,
  onClick,
  icon,
  text,
  isOverlay = false,
  isSpecial = false,
}) => {
  const baseClasses =
    "ay-font-medium ay-whitespace-nowrap ay-flex ay-items-center ay-gap-2 ay-transition-colors"
  const sizeClasses = isOverlay
    ? "ay-px-3 ay-py-2 ay-text-xs"
    : "ay-px-6 ay-py-4 ay-text-sm"

  let activeClasses
  if (active) {
    if (isSpecial) {
      activeClasses =
        "ay-border-b-2 ay-border-purple-500 ay-text-purple-600 ay-bg-purple-50"
    } else {
      activeClasses =
        "ay-border-b-2 ay-border-blue-500 ay-text-blue-600 ay-bg-blue-50"
    }
  } else {
    activeClasses =
      "ay-text-gray-500 hover:ay-text-gray-700 hover:ay-bg-gray-50"
  }

  return (
    <button
      className={`${baseClasses} ${sizeClasses} ${activeClasses}`}
      onClick={onClick}
    >
      <span className={isOverlay ? "ay-text-xs" : ""}>{icon}</span>
      <span className={isOverlay ? "ay-hidden sm:ay-inline" : ""}>{text}</span>
      {isSpecial && active && (
        <span className="ay-w-2 ay-h-2 ay-bg-purple-400 ay-rounded-full ay-animate-pulse"></span>
      )}
    </button>
  )
}

// MODIFICATO: Esporta il componente protetto con Basic Auth
export default withBasicAuth(AdminPanel)
